import React, { useState, useMemo } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { useLocation } from 'react-router'
import { useHistory } from 'react-router'
import { Redirect } from 'react-router-dom'
import { Body, FlexWrapper } from 'components/Shell/Shell'
import { Spinner } from 'components/Loaders/Loaders'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
import ReceiptIcon from '@tecuity/sos-icons/react/receipt'
import UserIcon from '@tecuity/sos-icons/react/user'
import ReceiptCheckIcon from '@tecuity/sos-icons/react/receipt-check'
import ReceiptCancelIcon from '@tecuity/sos-icons/react/receipt-cancel'
import ReceiptBatchIcon from '@tecuity/sos-icons/react/receipt-batch'
import DocumentIcon from '@tecuity/sos-icons/react/document'
import CreditCardCheckIcon from '@tecuity/sos-icons/react/credit-card-check'
import PlusHeavy from '@tecuity/sos-icons/react/plus-heavy'
import CheckBoxIcon from '@tecuity/sos-icons/react/checkbox'
import DollarSignDouble from '@tecuity/sos-icons/react/dollar-sign-double'
import '../Forms/Forms.scss'
import './Accounting.scss'
import ReceiptValidation from './ReceiptValidation/ReceiptValidation'
import Reconcile from './Reconcile/Reconcile'
import PayorAccounts from './FrequentPayorAccounts/PayorAccounts'

const frequentPayorLabel = process.env.FREQUENT_PAYOR_LABEL

const defaultCategories = [
  {
    title: 'Add',
    // categoryIcon: 'ReceiptIcon',
    tools: [
      {
        TITLE: 'New Cash/CC Receipt',
        redirect: '/accounting/receipt',
        icon: 'ReceiptIcon',
        userPermissions: ['ReceiptCreate'],
      },
      {
        TITLE: `Manage ${frequentPayorLabel || `Frequent Payor Account`}s`,
        redirect: '/accounting/payor',
        icon: 'UserIcon',
        userPermissions: ['ManageFrequentPayor', 'ReceiptCreate'],
      },
    ],
  },
  {
    title: 'Reconcile',
    // categoryIcon: "CheckBoxIcon",
    tools: [
      {
        TITLE: 'Reconcile Payment Batch',
        redirect: '/accounting/reconcile',
        subCategory: 'reconcile',
        icon: 'ReceiptCheckIcon',
        userPermissions: ['ReceiptReconcileBatch'],
      },
      {
        TITLE: 'Unreconcile Payment Batch',
        redirect: '/accounting/reconcile',
        subCategory: 'unreconcile',
        icon: 'ReceiptCancelIcon',
        userPermissions: ['ReceiptUnreconcile'],
      },
      {
        TITLE: 'Post Payment Batches',
        redirect: '/accounting/reconcile',
        subCategory: 'paymentBatches',
        icon: 'ReceiptBatchIcon',
        userPermissions: ['ReceiptCloseBatch'],
      }, //
      {
        TITLE: 'Post Distributions',
        redirect: '/accounting/reconcile',
        subCategory: 'distributions',
        icon: 'DocumentIcon',
        userPermissions: ['ReceiptCloseBatch'],
      },
      // close division period not implemented yet (RECEIPT_CLOSE_OTHER_USER_BATCH)
      // { // hidden due to bug 19615
      //   TITLE: "Reconcile Web Payments",
      //   redirect: "/accounting/reconcile",
      //   subCategory: 'reconcileWeb',
      //   icon: "CreditCardCheckIcon"
      // },
    ],
  },
]

const Icons = {
  ReceiptIcon: <ReceiptIcon />,
  UserIcon: <UserIcon />,
  ReceiptCheckIcon: <ReceiptCheckIcon />,
  ReceiptCancelIcon: <ReceiptCancelIcon />,
  ReceiptBatchIcon: <ReceiptBatchIcon />,
  DocumentIcon: <DocumentIcon />,
  CreditCardCheckIcon: <CreditCardCheckIcon />,
  PlusHeavy: <PlusHeavy />,
  CheckBoxIcon: <CheckBoxIcon />,
  DollarSignDouble: <DollarSignDouble />,
}

const Accounting = () => {
  const location = useLocation()
  const { appSettings } = useSelector((state) => state.app, shallowEqual)
  const [filter, setFilter] = useState('')
  const [loading /*setLoading*/] = useState(false)
  const [accountingCategories /*setToolCategories*/] = useState(defaultCategories)
  const [selectedSubCategory, setSelectedSubCategory] = useState(null)
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)

  return (
    <Body
      className={`forms ${location.pathname === '/accounting' ? '' : 'accounting'} ${
        location.pathname.includes('reconcile') ? 'reconcile' : ''
      }`}
    >
      <h1>Accounting</h1>
      {appSettings.WEBSITE_PORTAL_OFFLINE_YN === true && <Redirect to="/" />}
      {location.pathname === '/accounting' ? (
        <React.Fragment>
          <Toolbar filter={filter} onChange={setFilter} />
          <FlexWrapper direction="column" aria-busy={loading}>
            {loading ? (
              <div className="loading-wrapper forms-wrapper">
                <Spinner className="page-loader" themeColor="neutral" />
              </div>
            ) : (
              accountingCategories.map((ac) => {
                return (
                  <ToolsDumbCategory
                    filter={filter}
                    {...ac}
                    key={ac.title}
                    setSelectedSubCategory={setSelectedSubCategory}
                    tools={ac.tools.filter(
                      (tool) =>
                        !tool.userPermissions ||
                        tool.userPermissions.every((right) => appUserRights.includes(right))
                    )}
                  />
                )
              })
            )}
          </FlexWrapper>
        </React.Fragment>
      ) : null}
      {location.pathname.includes('receipt') ? <ReceiptValidation /> : null}
      {location.pathname.includes('payor') ? <PayorAccounts /> : null}
      {location.pathname.includes('reconcile') ? (
        <Reconcile
          selectedSubCategory={selectedSubCategory}
          setSelectedSubCategory={setSelectedSubCategory}
        />
      ) : null}
    </Body>
  )
}
export default Accounting

const Toolbar = ({ onChange, filter }) => {
  return (
    <div className="toolbar">
      <div className="filter-wrapper">
        <input
          type="text"
          className="filter"
          placeholder="Filter"
          value={filter}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Filter forms"
        />
        {filter && (
          <button aria-label="clear filter" className="clear" onClick={() => onChange('')}>
            <CrossIcon />
          </button>
        )}
      </div>
    </div>
  )
}

const ToolsDumbCategory = ({ filter, tools, title, categoryIcon = '', setSelectedSubCategory }) => {
  const filteredTools = useMemo(() => {
    return (tools || []).filter((tool) => tool.TITLE.toLowerCase().includes(filter.toLowerCase()))
  }, [filter, tools])

  return (
    filteredTools.length > 0 && (
      <div className="category">
        <div className="title-wrapper">
          <h2 className="title">{title}</h2>
        </div>
        <div className="form-blocks">
          {filteredTools.map(
            (tool) =>
              tool && (
                <ToolsDummyFormBlock
                  label={tool.TITLE}
                  categoryIcon={categoryIcon}
                  icon={tool.icon}
                  redirect={tool.redirect}
                  key={tool.TITLE}
                  subCategory={tool.subCategory || null}
                  setSelectedSubCategory={setSelectedSubCategory}
                />
              )
          )}
        </div>
      </div>
    )
  )
}

const ToolsDummyFormBlock = ({
  label,
  icon,
  redirect,
  children,
  subCategory,
  setSelectedSubCategory,
}) => {
  const history = useHistory()
  return (
    <div
      role="button"
      className={`form-block-wrapper`}
      onClick={() => {
        setSelectedSubCategory(subCategory)
        history.push(redirect)
      }}
    >
      <div className="form-block">
        <ToolsDummyFormIcon icon={icon} />
        <h3>
          <span className="title">{label}</span>
        </h3>
        {children}
      </div>
    </div>
  )
}

const ToolsDummyFormIcon = ({ style = {}, className = '', icon }) => {
  return (
    <div className={`form-block-icon-container ${className}`} style={style}>
      <div className="abbr-wrapper">{Icons[icon]}</div>
    </div>
  )
}
